/**
 * Find the shortest route (with least turns) between two points on the path network.
 *
 * @param {Array} roundedPaths - Array of path objects with .allPoints and .id
 * @param {Array} startCoord - [lat, lng] of start point (will snap to nearest path point)
 * @param {Array} endCoord - [lat, lng] of end point (will snap to nearest path point)
 * @returns {{ path: Array, startPoint: Array, endPoint: Array, direction: string|null } | null}
 */
export function findPath(roundedPaths, startCoord, endCoord) {
	if (!roundedPaths || !roundedPaths.length || !startCoord || !endCoord) return null;
	if (!startCoord.every(Number.isFinite) || !endCoord.every(Number.isFinite)) return null;

	const start = [Math.round(startCoord[0]), Math.round(startCoord[1])];
	const end = [Math.round(endCoord[0]), Math.round(endCoord[1])];

	const keyOf = (point) => `${point[0]},${point[1]}`;

	// 每一個座標分別落在哪幾條路徑上。舊版是四層巢狀迴圈兩兩比對所有點，
	// 在 3700 個點的樓層要比一千三百萬次；這裡掃一遍就好。
	const pathsAtPoint = new Map();
	roundedPaths.forEach((path, pathIndex) => {
		path.allPoints.forEach((point) => {
			const key = keyOf(point);
			let owners = pathsAtPoint.get(key);
			if (!owners) {
				owners = { point, paths: [] };
				pathsAtPoint.set(key, owners);
			}
			if (!owners.paths.includes(pathIndex)) owners.paths.push(pathIndex);
		});
	});

	const snap = (target) => {
		let closestDistance = null;
		let closestPoint = null;
		roundedPaths.forEach((path) => {
			path.allPoints.forEach((point) => {
				const distance = Math.sqrt(Math.pow(point[0] - target[0], 2) + Math.pow(point[1] - target[1], 2));
				// 舊版寫 `!closestDistance`，距離剛好是 0 的時候會被當成「還沒找到」而一直被後面的點蓋掉。
				if (closestDistance === null || distance < closestDistance) {
					closestDistance = distance;
					closestPoint = point;
				}
			});
		});
		return closestPoint;
	};

	const closestPointToStart = snap(start);
	const closestPointToEnd = snap(end);
	if (!closestPointToStart || !closestPointToEnd) return null;

	// 轉乘點＝同時屬於兩條以上路徑的座標，再加上起點與終點本身。
	const nodeIndexByKey = new Map();
	const nodePoints = [];
	const addNode = (point) => {
		const key = keyOf(point);
		if (nodeIndexByKey.has(key)) return nodeIndexByKey.get(key);
		const index = nodePoints.length;
		nodeIndexByKey.set(key, index);
		nodePoints.push(point);
		return index;
	};

	const startNode = addNode(closestPointToStart);
	const endNode = addNode(closestPointToEnd);
	pathsAtPoint.forEach((owners) => {
		if (owners.paths.length > 1) addNode(owners.point);
	});

	// 一條路徑上的所有節點彼此可以直達，走的就是那條路徑本身。
	const nodesOnPath = roundedPaths.map(() => []);
	nodeIndexByKey.forEach((nodeIndex, key) => {
		const owners = pathsAtPoint.get(key);
		if (!owners) return;
		owners.paths.forEach((pathIndex) => nodesOnPath[pathIndex].push(nodeIndex));
	});

	const manhattan = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);

	const neighbours = nodePoints.map(() => new Map());
	nodesOnPath.forEach((nodes) => {
		for (let i = 0; i < nodes.length; i++) {
			for (let j = i + 1; j < nodes.length; j++) {
				const a = nodes[i];
				const b = nodes[j];
				const cost = manhattan(nodePoints[a], nodePoints[b]);
				const existingA = neighbours[a].get(b);
				if (existingA === undefined || cost < existingA) neighbours[a].set(b, cost);
				const existingB = neighbours[b].get(a);
				if (existingB === undefined || cost < existingB) neighbours[b].set(a, cost);
			}
		}
	});

	// 先比轉乘次數、再比距離：跟舊版「先挑轉折最少、再挑最短」的規則一樣，
	// 但用 Dijkstra 一次算完，不用窮舉所有排列組合。
	const INF = Number.POSITIVE_INFINITY;
	const hops = nodePoints.map(() => INF);
	const dist = nodePoints.map(() => INF);
	const prev = nodePoints.map(() => -1);
	const done = nodePoints.map(() => false);
	hops[startNode] = 0;
	dist[startNode] = 0;

	for (;;) {
		let current = -1;
		for (let i = 0; i < nodePoints.length; i++) {
			if (done[i] || hops[i] === INF) continue;
			if (current === -1 || hops[i] < hops[current] || (hops[i] === hops[current] && dist[i] < dist[current])) current = i;
		}
		if (current === -1 || current === endNode) break;
		done[current] = true;

		neighbours[current].forEach((cost, next) => {
			if (done[next]) return;
			const nextHops = hops[current] + 1;
			const nextDist = dist[current] + cost;
			if (nextHops < hops[next] || (nextHops === hops[next] && nextDist < dist[next])) {
				hops[next] = nextHops;
				dist[next] = nextDist;
				prev[next] = current;
			}
		});
	}

	if (hops[endNode] === INF) return null;

	const shortestRoute = [];
	for (let node = endNode; node !== -1; node = prev[node]) shortestRoute.unshift(nodePoints[node]);

	const lastPoint = shortestRoute[shortestRoute.length - 1];
	const secondLastPoint = shortestRoute[shortestRoute.length - 2];

	let direction = null;
	const dLat = (lastPoint?.[0] || 0) - (secondLastPoint?.[0] || 0);
	const dLng = (lastPoint?.[1] || 0) - (secondLastPoint?.[1] || 0);

	if (Math.abs(dLng) >= Math.abs(dLat)) {
		direction = dLng > 0 ? 'right' : 'left';
	} else {
		direction = dLat > 0 ? 'up' : 'down';
	}

	return {
		path: shortestRoute,
		startPoint: closestPointToStart,
		endPoint: closestPointToEnd,
		direction,
	};
}

/**
 * Find the closest point on any path to a given coordinate.
 */
export function findClosestPathPoint(roundedPaths, coord) {
	const target = [Math.round(coord[0]), Math.round(coord[1])];
	let closestDistance = null;
	let closestPoint = null;

	roundedPaths.forEach((path) => {
		path.allPoints.forEach((point) => {
			const distance = Math.sqrt(Math.pow(point[0] - target[0], 2) + Math.pow(point[1] - target[1], 2));
			if (closestDistance === null || distance < closestDistance) {
				closestDistance = distance;
				closestPoint = point;
			}
		});
	});

	return closestPoint;
}
